import { failure, parseWeight, shiftDate, weightMetrics } from '../domain/progress.ts';
import { validLocalDate } from '../domain/training.ts';
import type { Period, ProgressResult } from '../domain/progress.ts';
import type { ProgressRepository, WeightCommand, WeightView } from '../ports/progress-repository.ts';
export type ProgressIntentResult<T> = ProgressResult<T> & { committed?: true };
export interface ProgressIntent<T> { run(): Promise<ProgressIntentResult<T>> }
export interface WeightFields { localDate: string; recordedInstant: string; value: string }
export class ProgressWeight {
  constructor(private repository: ProgressRepository, private id: () => string) {}
  listWeightObservations(period: Period) { return this.repository.listWeights(period); }
  getWeightObservation(id: string) { return this.repository.readWeight(id); }
  getWeightHistory(id: string) { return this.repository.readWeightHistory(id); }
  parse(raw: string) { return parseWeight(raw); }
  async getWeightTrend(date: string) {
    if (!validLocalDate(date) || date < '0100-01-14') return failure('VALIDATION_ERROR');
    const weights = await this.repository.listWeights({startLocalDate:shiftDate(date,-13),endLocalDate:date});
    return weights.ok ? weightMetrics(weights.value.map(v => v.observation), date) : weights;
  }
  getWeightComparison(date: string) { return this.getWeightTrend(date); }
  recordWeight(fields: WeightFields) { return this.intent(fields); }
  correctWeight(view: WeightView, fields: WeightFields) { return this.intent(fields, view); }
  private intent(fields: WeightFields, observed?: WeightView): ProgressIntent<WeightView> {
    const draft = structuredClone(fields), view = observed ? structuredClone(observed) : undefined;
    let command: WeightCommand | undefined, committed = false, running: Promise<ProgressIntentResult<WeightView>> | undefined;
    const execute = async (): Promise<ProgressIntentResult<WeightView>> => {
      if (!command) {
        const value = parseWeight(draft.value); if (!value.ok) return value;
        const context = view ? { ok: true as const, value: view.context } : await this.repository.readContext();
        if (!context.ok) return context;
        command = { context: context.value, operationId: this.id(), expectedRevision: view?.observation.revision ?? 0,
          observation: { observationId: view?.observation.observationId ?? this.id(), revision: (view?.observation.revision ?? 0) + 1,
            localDate: draft.localDate, recordedInstant: draft.recordedInstant, unit:'kg', value:value.value } };
      }
      if (!committed) {
        const result = await (view ? this.repository.correctWeight(command) : this.repository.recordWeight(command));
        if (!result.ok) return result;
        committed = true;
      }
      const result = await this.repository.readWeight(command.observation.observationId);
      return result.ok ? result : { ...result, committed: true };
    };
    return { run() { running ??= execute().finally(() => { running = undefined; }); return running; } };
  }
}
